import { z } from "zod";
const arabicName = z
  .string()
  .min(3, "الاسم العربي يجب أن يكون على الأقل 3 أحرف")
  .max(100, "الاسم العربي يجب ألا يتجاوز 100 حرف")
  .regex(
    /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s]+$/,
    "الاسم العربي يجب أن يحتوي على أحرف عربية فقط"
  )
  .trim();
const englishName = z
  .string()
  .min(3, "English name must be at least 3 characters")
  .max(100, "English name must not exceed 100 characters")
  .regex(/^[A-Za-z\s]+$/, "English name must contain only English letters")
  .trim();
const arabicDesc = z
  .string()
  .min(10, "الوصف العربي يجب أن يكون على الأقل 10 أحرف")
  .max(5000, "الوصف العربي يجب ألا يتجاوز 5000 حرف")
  .regex(
    /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s\p{P}\p{S}\p{N}]*$/u,
    "الوصف العربي يجب أن يحتوي على أحرف عربية فقط"
  )
  .trim();
const englishDesc = z
  .string()
  .min(10, "English description must be at least 10 characters")
  .max(5000, "English description must not exceed 5000 characters")
  .regex(
    /^[\p{L}\p{M}\s\p{P}\p{S}\p{N}]*$/u,
    "English description must contain only valid characters"
  )
  .trim();
const phoneNumber = z
  .string()
  .min(8, "Phone number must be at least 8 digits")
  .max(15, "Phone number must not exceed 15 digits")
  .regex(/^[+]?[\d\s\-()]+$/, "Invalid phone number format")
  .transform((val) => val.replace(/\s/g, ""));
export const emergencyNumber = z.object({
  name_ar: arabicName,

  name_en: englishName,

  phone: phoneNumber,

  description_ar: arabicDesc,

  description_en: englishDesc,
  active: z.boolean().optional().default(true),
});
const photo = z
  .union([
    z
      .instanceof(FileList)
      .transform((fileList) => fileList?.[0]) // Extract first file
      .refine(
        (file) =>
          file &&
          ["image/svg+xml", "image/png", "image/jpeg", "image/webp"].includes(
            file.type
          ),
        {
          message: "image must be SVG, PNG, JPG, or WebP image",
        }
      ),
    z
      .instanceof(File)
      .refine(
        (file) =>
          ["image/svg+xml", "image/png", "image/jpeg", "image/webp"].includes(
            file.type
          ),
        {
          message: "image must be SVG, PNG, JPG, or WebP image",
        }
      ),
    z.string(),
  ])
  .optional();
export const privacySchema = z.object({
  name_ar: arabicName,
  name_en: englishName,
  text_ar: arabicDesc,
  text_en: englishDesc,
  color: z
    .string()
    .regex(
      /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
      "Must be a valid hex color (e.g., #FF5733 or #F93)"
    ),
  icon: photo,
  active: z.number().optional(),
});
export const adsSchema = z.object({
  title: z
    .string()
    .min(3, { message: "Title must be at least 3 characters" })
    .max(100, { message: "Title must be less than 100 characters" })
    .trim(),

  description: z
    .string()
    .min(10, { message: "Description must be at least 10 characters" })
    .max(500, { message: "Description must be less than 500 characters" })
    .trim(),

  cta_link: z.string().url({ message: "Please enter a valid URL" }).trim(),

  channel: z.enum(["passenger_app", "driver_app", "both"], {
    errorMap: () => ({ message: "Please select a valid channel" }),
  }),

  target_type: z.enum(["passenger", "driver", "all"], {
    errorMap: () => ({ message: "Please select a valid target type" }),
  }),

  target_location: z
    .string()
    .min(1, { message: "Location cannot be empty if provided" })
    .max(100, { message: "Location must be less than 100 characters" })
    .trim()
    .optional()
    .or(z.literal("")),

  target_interests: z.string().optional().or(z.literal("")),

  end_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
      message: "Date must be in YYYY-MM-DD format",
    })
    .refine(
      (date) => {
        const selectedDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate > today;
      },
      { message: "End date must be after today" }
    ),

  media: z
    .any()
    .optional()
    .refine(
      (file) => {
        // If it's undefined or null, it's valid
        if (!file) return true;
        // If it's a string (existing media URL), it's valid
        if (typeof file === "string") return true;
        // If it's a File, validate it
        if (file instanceof File) {
          // Check file size
          if (file.size > 10 * 1024 * 1024) return false;

          // Check file type
          const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
            "image/webp",
            "video/mp4",
            "video/webm",
            "video/quicktime",
            "video/x-msvideo",
          ];

          return allowedTypes.includes(file.type);
        }
        return false;
      },
      {
        message:
          "File must be a valid image/video (max 10MB) or leave empty to keep existing",
      }
    ),
});
export const occasionSchema = z.object({
  name_ar: arabicName,
  name_en: englishName,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Date must be in YYYY-MM-DD format",
  }),
  points_per_user: z
    .number({
      required_error: "Points is required",
      invalid_type_error: "Points must be a number",
    })
    .int({ message: "Points must be an integer" })
    .nonnegative({ message: "Points cannot be negative" })
    .max(1000, { message: "Points cannot exceed 1,000" })
    .refine((val) => val !== undefined && val !== null, {
      message: "Points is required",
    }),
});
export const rewardSchema = z.object({
  name_ar: arabicName,
  name_en: englishName,
  description_ar: arabicDesc,
  description_en: englishDesc,
  type: z.enum(["external_product", "wallet_credit"], {
    errorMap: () => ({ message: "Please select a valid reward type" }),
  }),
  required_points: z
    .number({
      required_error: "Required points is required",
      invalid_type_error: "Required points must be a number",
    })
    .int({ message: "Required points must be an integer" })
    .positive({ message: "Required points must be a positive number" })
    .max(1000000, { message: "Required points cannot exceed 1,000,000" }),
  discount_percent: z
    .union([
      z.number().int().min(0).max(100),
      z.nan(),
      z.literal(""),
      z.null(),
      z.undefined(),
    ])
    .transform((val) => {
      if (val === "" || val === null || Number.isNaN(val)) {
        return undefined;
      }
      return val;
    })
    .optional(),
  discount_amount: z
    .union([
      z.number().positive().max(1000000),
      z.nan(),
      z.literal(""),
      z.null(),
      z.undefined(),
    ])
    .transform((val) => {
      if (val === "" || val === null || Number.isNaN(val)) {
        return undefined;
      }
      return val;
    })
    .optional(),
  active: z
    .union([
      z
        .string()
        .transform((val) => val === "1" || val === "true" || val === "on"),
      z.number().transform((val) => val === 1),
      z.boolean(),
    ])
    .optional()
    .default(true)
    .transform((val) => (val ? 1 : 0)),
  image: z
    .union([
      z
        .instanceof(FileList)
        .transform((fileList) => fileList?.[0])
        .refine(
          (file) =>
            !file ||
            ["image/svg+xml", "image/png", "image/jpeg", "image/webp"].includes(
              file.type
            ),
          {
            message: "Image must be SVG, PNG, JPG, or WebP image",
          }
        )
        .optional(),
      z
        .instanceof(File)
        .refine(
          (file) =>
            ["image/svg+xml", "image/png", "image/jpeg", "image/webp"].includes(
              file.type
            ),
          {
            message: "Image must be SVG, PNG, JPG, or WebP image",
          }
        )
        .optional(),
      z.string().url({ message: "Image must be a valid URL" }).optional(),
      z.null().optional(),
      z.undefined().optional(),
    ])
    .optional()
    .default(null),
});
export const distputTypeSchema = z.object({
  name_ar: arabicName,
  name_en: englishName,
  is_active: z.boolean().optional(),
});

export const appVersionCreateSchema = z.object({
  platform: z.enum(["ios", "android"], {
    required_error: "Platform is required",
  }),
  version: z
    .string()
    .min(1, "Version is required")
    .regex(/^\d+(\.\d+)*$/, "Invalid version format (e.g. 1.0.4)"),
  build_number: z.string().min(1, "Build number is required"),
  force_update: z.boolean(),
  release_notes_ar: z
    .string()
    .min(3, "Arabic release notes must be at least 3 characters"),
  release_notes_en: z
    .string()
    .min(3, "English release notes must be at least 3 characters"),
  download_url: z.string().url("Download URL must be valid"),
  is_active: z.boolean(),
  released_at: z.string().min(1, "Release date is required"),
});

export const appVersionUpdateSchema = z.object({
  version: z
    .string()
    .min(1, "Version is required")
    .regex(/^\d+(\.\d+)*$/, "Invalid version format (e.g. 1.0.4)"),
  build_number: z.string().min(1, "Build number is required"),
  force_update: z.boolean(),
  release_notes_ar: z
    .string()
    .min(3, "Arabic release notes must be at least 3 characters"),
  release_notes_en: z
    .string()
    .min(3, "English release notes must be at least 3 characters"),
  is_active: z.boolean(),
});
